import {FC, useEffect, useState} from 'react'
import {useIntl} from "react-intl";
import { useDispatch, useSelector } from 'react-redux';
import { PackageSidebar } from './Sidebar/PackageSidebar';
import { RootState } from '../../../core/store/store';
import { useParams } from 'react-router-dom';
import findCompanyPackageByIdRequest from '../../../core/api/requests/companies/package/findCompanyPackageByIdRequest';
import {setGuestEmail, setPackage} from "../../../core/store/slices/package.ts";
import ITalent from '../../../core/interfaces/ITalent.ts';
import TalentBigButton from '../../../utils/TalentBigButton';
import ModalTalentDetails from './Modal/ModalTalentDetails.tsx';
import { Messages } from '../../packages/components/Messages.tsx';

const PackageView: FC = () => {
    const [talents, setTalents] = useState<ITalent[]>([])
    const [selectedTalent, setSelectedTalent] = useState<ITalent | null>(null)
    const [showMobileChat, setShowMobileChat] = useState<boolean>(false)
    const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 992)
    const intl = useIntl()
    const dispatch = useDispatch()
    const params = useParams()

    const pkg = useSelector((state: RootState) => state.package)

    const findByID = async (id: string, companyId: string, emailBase64?: string) => {
        if (!id) return

        const response = await findCompanyPackageByIdRequest(
            dispatch,
            id,
            companyId,
            emailBase64
        )

        if (response.statusCode !== 200) return
        dispatch(setPackage(response.data))
    }

    const selectTalent = (talent:ITalent) => {
        setSelectedTalent(talent)
    }

    const downloadFile = () => {
        if (!params.id || !params.cid) return
        const url = import.meta.env.VITE_APP_API_URL
        const pdfUrl = `${url}/p/company/${params.cid}/packages/${params.id}/pdf?talents=all`
        window.open(pdfUrl, '_blank')
    }

    useEffect(() => {
        if(pkg?.package?.talents){
            let talentArray = (pkg?.package?.talents || []).map((talent) => {
                const mediaFiles = (talent.user?.talent?.media?.files || [])
                  .filter((f: any) => !f.type?.startsWith('video.'))
                const selectedImages = talent.images && talent.images.length > 0
                  ? talent.images
                  : mediaFiles.map((f: any) => f.url)

                return {
                  ...talent.user,
                  status: talent.status,
                  images: selectedImages,
                  profileImage: talent.profileImage || talent.user.profileImage,
                }
              })
            setTalents(talentArray)
        }
    },[pkg])

    useEffect(() => {
        if (params.id && params.cid && params.email) {
            findByID(params.id, params.cid, params.email)

            const decodedEmail = atob(params.email)
            dispatch(setGuestEmail(decodedEmail))
        }
    }, [])

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 992)
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

  return (
    <>
        <div className='app-wrapper flex-column flex-row-fluid' id='kt_app_wrapper'>
            {!isMobile && <PackageSidebar />}

            <div className='app-main public flex-column flex-row-fluid p-12' id='kt_app_main'>
                {isMobile &&
                    <div className="mobile-package-header d-flex align-items-center justify-content-between mb-4">
                        <img alt="Logo" src="/media/logos/default-small.svg" className="h-25px" />
                        {pkg?.package?.name && <span className="fw-semibold text-gray-700 fs-7">{pkg.package.name}</span>}
                    </div>
                }
                <div className='d-flex flex-column flex-column-fluid'>
                    {pkg?.package?.public === false ?
                        <div className='alert alert-warning d-flex align-items-center p-5'>
                            <i className='fa-solid fa-lock fs-2 me-4 text-warning'></i>
                            <div className='d-flex flex-column'>
                                <span className='fw-bold fs-5'>{intl.formatMessage({id: 'PACKAGE.NOT_PUBLIC_TITLE'})}</span>
                                <span className='fs-7'>{intl.formatMessage({id: 'PACKAGE.NOT_PUBLIC_DESCRIPTION'})}</span>
                            </div>
                        </div>
                    :
                        talents.length > 0 &&
                            <div className={'row gx-1'}>
                            {(talents || []).map((talent) => {
                                return <TalentBigButton
                                key={talent.id}
                                talent={talent}
                                companyId={params.cid}
                                packageId={params.id}
                                showDetails={true}
                                allowSelect={true}
                                publicView={true}
                                onSelect={() => selectTalent(talent)}
                                />
                            })}
                            </div>
                    }
                </div>
            </div>

            {!!selectedTalent &&
                <ModalTalentDetails
                    show={!!selectedTalent}
                    onClose={() => setSelectedTalent(null)}
                    selectedTalent={selectedTalent}
                />
            }

            {/* Mobile bottom bar - visible only on mobile */}
            <div className="mobile-bottom-bar d-lg-none">
                {pkg?.package?.public !== false &&
                    <button className="btn btn-mobile-action" onClick={() => downloadFile()}>
                        <i className="las la-download fs-1"></i>
                        <span>{intl.formatMessage({id: 'PLACEHOLDERS.DOWNLOAD_PACKAGE'})}</span>
                    </button>
                }
                <button className="btn btn-mobile-action" onClick={() => setShowMobileChat(!showMobileChat)}>
                    <i className="las la-comments fs-1"></i>
                    <span>{intl.formatMessage({id: 'PLACEHOLDERS.CHAT'})}</span>
                </button>
            </div>

            {/* Mobile chat panel */}
            {showMobileChat &&
                <div className="mobile-chat-panel d-lg-none">
                    <div className="mobile-chat-header d-flex justify-content-between align-items-center p-3 border-bottom">
                        <h5 className="m-0">{intl.formatMessage({id: 'PLACEHOLDERS.CHAT'})}</h5>
                        <button className="btn btn-sm btn-icon btn-active-light-primary" onClick={() => setShowMobileChat(false)}>
                            <i className="fa-solid fa-xmark fs-3"></i>
                        </button>
                    </div>
                    <div className="mobile-chat-body flex-grow-1">
                        <Messages />
                    </div>
                </div>
            }
        </div>
    </>

  )
}

export {PackageView}
